<template>
  <div>
    <div class="flex justify-end mb-4">
      <vs-button 
        v-if="!isEdit"
        color="#009FB7" 
        type="border" 
        icon-pack="feather" 
        icon="icon-edit" 
        @click="toggleEdit"
      >
        Edit
      </vs-button>
      <vs-button 
        v-else
        color="danger" 
        type="filled" 
        icon-pack="feather" 
        icon="icon-x" 
        @click="cancelEdit"
      >
        Cancel
      </vs-button>
    </div>

    <vx-card class="mb-8">
      <!-- Server Monitoring Section -->
      <div>
        <h3 class="text-lg pln-color font-semibold mb-6">Server Monitoring</h3>
        
        <div class="vx-row mb-6">
          <div class="vx-col sm:w-1/3 w-full">
            <span>Email Server</span>
          </div>
          <div class="vx-col sm:w-2/5 w-full">
            <vs-input 
              class="w-full" 
              v-model="emailServer"
              :disabled="!isEdit"
              type="email"
            />
          </div>
        </div>

        <div class="vx-row mb-6">
          <div class="vx-col sm:w-1/3 w-full">
            <span>Password Server</span>
          </div>
          <div class="vx-col sm:w-2/5 w-full">
            <vs-input 
              class="w-full" 
              v-model="passwordServer"
              :disabled="!isEdit"
              type="password"
            />
          </div>
        </div>

        <div class="vx-row mb-6">
          <div class="vx-col sm:w-1/3 w-full">
            <span>Sender Name</span>
          </div>
          <div class="vx-col sm:w-2/5 w-full">
            <vs-input 
              class="w-full" 
              v-model="senderName"
              :disabled="!isEdit"
            />
          </div>
        </div>

        <div class="flex justify-end gap-2" v-if="isEdit">
          <vs-button 
            v-if="emailServerModified || passwordServerModified"
            color="#009FB7" 
            type="border" 
            icon-pack="feather" 
            icon="icon-check" 
            @click.prevent="verifyEmailCredentials"
          >
            Periksa
          </vs-button>
          <vs-button 
            color="#009FB7" 
            type="filled" 
            icon-pack="feather" 
            icon="icon-save" 
            @click.prevent="saveServerMonitoring"
            :disabled="(emailServerModified || passwordServerModified) && !emailCredentialsVerified"
          >
            Save
          </vs-button>
        </div>
      </div>

      <!-- Separator -->
      <hr class="my-8" style="border: none; border-top: 2px solid #ccc;">

      <!-- Cleaning Log Section -->
      <div>
        <h3 class="text-lg pln-color font-semibold mb-6">Cleaning Log</h3>
        
        <div class="vx-row mb-6">
          <div class="vx-col sm:w-1/3 w-full">
            <span>Cleaning Duration</span>
          </div>
          <div class="vx-col sm:w-2/5 w-full">
            <v-select 
              v-model="cleaningDuration"
              :options="cleaningDurationOptions"
              :disabled="!isEdit"
              class="w-full"
            />
          </div>
        </div>

        <div class="flex justify-end" v-if="isEdit">
          <vs-button 
            color="#009FB7" 
            type="filled" 
            icon-pack="feather" 
            icon="icon-save" 
            @click.prevent="saveCleaningLog"
          >
            Save
          </vs-button>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import api from "@/api";
import vSelect from "vue-select";

export default {
  components: {
    vSelect,
  },
  data() {
    return {
      isEdit: false,
      emailServer: "",
      passwordServer: "",
      senderName: "",
      emailServerOriginal: "",
      passwordServerOriginal: "",
      emailCredentialsVerified: false,
      cleaningDuration: null,
      cleaningDurationOptions: [
        { label: "6 Months", value: { intervalValue: 6, intervalType: "month" } },
        { label: "1 Year", value: { intervalValue: 1, intervalType: "year" } },
        { label: "2 Years", value: { intervalValue: 2, intervalType: "year" } },
        { label: "3 Years", value: { intervalValue: 3, intervalType: "year" } },
      ],
    };
  },
  computed: {
    emailServerModified() {
      return this.emailServer !== this.emailServerOriginal;
    },
    passwordServerModified() {
      return this.passwordServer !== this.passwordServerOriginal;
    },
  },
  mounted() {
    this.loadCleansingSchedule();
    this.loadServerGeneral();
  },
  methods: {
    loadServerGeneral() {
      this.$vs.loading();
      api
        .getServerGeneral()
        .then((result) => result.data)
        .then((data) => {
          console.log("✅ [loadServerGeneral] Response:", data);
          if (data.status && data.data && data.data.length > 0) {
            const serverData = data.data[0];
            this.emailServer = serverData.email || "";
            this.emailServerOriginal = serverData.email || "";
            this.passwordServer = serverData.password || "";
            this.passwordServerOriginal = serverData.password || "";
            this.senderName = serverData.username || "";
          }
          this.$vs.loading.close();
        })
        .catch((e) => {
          console.error("❌ [loadServerGeneral] Error:", e);
          this.$vs.loading.close();
        });
    },
    loadCleansingSchedule() {
      this.$vs.loading();
      api
        .getCleansingGeneral()
        .then((result) => result.data)
        .then((data) => {
          console.log("✅ [loadCleansingSchedule] Response:", data);
          if (data.status && data.data && data.data.length > 0) {
            const scheduleData = data.data[0];
            // Find the matching option from cleaningDurationOptions
            const matchedOption = this.cleaningDurationOptions.find(
              (option) =>
                option.value.intervalValue === scheduleData.intervalValue &&
                option.value.intervalType === scheduleData.intervalType
            );
            this.cleaningDuration = matchedOption || null;
          }
          this.$vs.loading.close();
        })
        .catch((e) => {
          console.error("❌ [loadCleansingSchedule] Error:", e);
          this.$vs.loading.close();
        });
    },
    toggleEdit() {
      this.isEdit = true;
    },
    cancelEdit() {
      this.isEdit = false;
      this.emailCredentialsVerified = false;
      this.loadCleansingSchedule();
      this.loadServerGeneral();
    },
    saveServerMonitoring() {
      if (!this.validateServer()) {
        return;
      }

      // If email or password was modified, they must be verified first
      if ((this.emailServerModified || this.passwordServerModified) && !this.emailCredentialsVerified) {
        this.$vs.notify({
          title: "Validation Error",
          text: "Please verify email and password before saving",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      this.$vs.loading();
      const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
      const params = {
        userId: userInfo.id || 0,
        email: this.emailServer,
        username: this.senderName,
        password: this.passwordServer,
      };

      console.log("📦 [saveServerMonitoring] API Params:", params);

      api
        .postServerGeneral(params)
        .then((result) => result.data)
        .then((data) => {
          console.log("✅ [saveServerMonitoring] Response:", data);
          if (data.status) {
            this.$vs.notify({
              title: "Success",
              text: "Server monitoring configuration saved",
              color: "success",
              position: "top-right",
            });
            this.isEdit = false;
            this.emailCredentialsVerified = false;
            this.loadServerGeneral();
          } else {
            this.$vs.notify({
              title: "Error",
              text: data.message || "Failed to save configuration",
              color: "danger",
              position: "top-right",
            });
          }
          this.$vs.loading.close();
        })
        .catch((e) => {
          console.error("❌ [saveServerMonitoring] Error:", e);
          this.$vs.notify({
            title: "Error",
            text: "Failed to save configuration",
            color: "danger",
            position: "top-right",
          });
          this.$vs.loading.close();
        });
    },
    verifyEmailCredentials() {
      if (!this.emailServer.trim() || !this.passwordServer.trim()) {
        this.$vs.notify({
          title: "Validation Error",
          text: "Please enter both email and password",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      this.$vs.loading();
      const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
      const params = {
        userId: userInfo.id || 0,
        email: this.emailServer,
        password: this.passwordServer,
      };

      console.log("📦 [verifyEmailCredentials] API Params:", params);

      api
        .postServerGeneralCheck(params)
        .then((result) => {
          console.log("✅ [verifyEmailCredentials] Response Status:", result.status);
          console.log("✅ [verifyEmailCredentials] Response Data:", result.data);
          if (result.status === 200) {
            this.emailCredentialsVerified = true;
            this.$vs.notify({
              title: "Success",
              text: "Email credentials verified successfully",
              color: "success",
              position: "top-right",
            });
          } else {
            this.emailCredentialsVerified = false;
            this.$vs.notify({
              title: "Error",
              text: result.data.message || "Email credentials verification failed",
              color: "danger",
              position: "top-right",
            });
          }
          this.$vs.loading.close();
        })
        .catch((e) => {
          console.error("❌ [verifyEmailCredentials] Error:", e);
          this.emailCredentialsVerified = false;
          
          // Handle 400 Bad Request with proper error message
          let errorMessage = "Failed to verify email credentials. Please check your email and password and retry";
          if (e.response && e.response.status === 400 && e.response.data) {
            errorMessage = e.response.data.message || "Invalid email or password";
          } else if (e.response && e.response.data && e.response.data.message) {
            errorMessage = e.response.data.message;
          }
          
          this.$vs.notify({
            title: "Error",
            text: errorMessage,
            color: "danger",
            position: "top-right",
          });
          this.$vs.loading.close();
        });
    },
    saveCleaningLog() {
      if (!this.cleaningDuration) {
        this.$vs.notify({
          title: "Validation Error",
          text: "Please select cleaning duration",
          color: "danger",
          position: "top-right",
        });
        return;
      }

      this.$vs.loading();
      const params = {
        intervalValue: this.cleaningDuration.value ? this.cleaningDuration.value.intervalValue : this.cleaningDuration.intervalValue,
        intervalType: this.cleaningDuration.value ? this.cleaningDuration.value.intervalType : this.cleaningDuration.intervalType,
      };

      console.log("📦 [saveCleaningLog] API Params:", params);

      api
        .postCleansingGeneral(params)
        .then((result) => result.data)
        .then((data) => {
          console.log("✅ [saveCleaningLog] Response:", data);
          if (data.status) {
            this.$vs.notify({
              title: "Success",
              text: "Cleaning log configuration saved",
              color: "success",
              position: "top-right",
            });
            this.isEdit = false;
          } else {
            this.$vs.notify({
              title: "Error",
              text: data.message || "Failed to save configuration",
              color: "danger",
              position: "top-right",
            });
          }
          this.$vs.loading.close();
        })
        .catch((e) => {
          console.error("❌ [saveCleaningLog] Error:", e);
          this.$vs.notify({
            title: "Error",
            text: "Failed to save configuration",
            color: "danger",
            position: "top-right",
          });
          this.$vs.loading.close();
        });
    },
    validateServer() {
      if (!this.emailServer.trim()) {
        this.$vs.notify({
          title: "Validation Error",
          text: "Please enter email server",
          color: "danger",
          position: "top-right",
        });
        return false;
      }
      if (!this.passwordServer.trim()) {
        this.$vs.notify({
          title: "Validation Error",
          text: "Please enter password server",
          color: "danger",
          position: "top-right",
        });
        return false;
      }
      if (!this.senderName.trim()) {
        this.$vs.notify({
          title: "Validation Error",
          text: "Please enter sender name",
          color: "danger",
          position: "top-right",
        });
        return false;
      }
      return true;
    },
  },
};
</script>

<style scoped>
.vx-row {
  margin-bottom: 0;
}

.vx-col {
  padding-bottom: 0;
}
</style>
